import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import { useTranslations } from '../../../contexts/translation.context';
import { Typography } from '../../../components';
import { FilterSelect, ModelSearchSelect } from '../../../components/ui';
import {
  SearchInputIc,
  ResetFiltersIc,
  DangerIc,
} from '../../../components/icons';
import { showError } from '../../../utils/toast';
import { parseApiError } from '../../../utils/errorParser';
import { RevshareCampaignsTable } from './components/RevshareCampaignsTable';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import {
  refShareApi,
  RefShareOrder,
  OrderStatus,
  OwnerType,
  TrafficSource,
} from '../../../api/services';
import staffApi, { OnlyFansAccount } from '../../../api/staff';

const RevshareCampaignsPage: React.FC = () => {
  const { t } = useTranslations();

  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.revshareCampaigns'), isActive: true },
  ], [t]);

  usePageBreadcrumbs(breadcrumbs);

  // Data state
  const [orders, setOrders] = useState<RefShareOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPlatformIncome, setTotalPlatformIncome] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [campaignTypeFilter, setCampaignTypeFilter] = useState<string>('all');
  const [ownerTypeFilter, setOwnerTypeFilter] = useState<string>('all');
  const [modelFilter, setModelFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Sorting state (created_at = newest first, -created_at = oldest first on backend)
  const [sortBy, setSortBy] = useState<string>('created_at');

  // Available models for filter (OnlyFans accounts)
  const [onlyFansAccounts, setOnlyFansAccounts] = useState<OnlyFansAccount[]>([]);
  const [modelSearchTerm, setModelSearchTerm] = useState('');
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  // Helper to check if filter value is valid (not 'all', undefined, or empty)
  const isValidFilter = (value: string | undefined | null): boolean => {
    return !!value && value !== 'all' && value !== 'undefined';
  };

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Record<string, unknown> = {
        page: currentPage,
        page_size: pageSize,
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (isValidFilter(statusFilter)) {
        params.status = statusFilter as OrderStatus;
      }

      if (isValidFilter(ownerTypeFilter)) {
        params.type = ownerTypeFilter as OwnerType;
      }

      if (isValidFilter(modelFilter)) {
        params.model = parseInt(modelFilter, 10);
      }

      if (isValidFilter(sourceFilter)) {
        params.source = sourceFilter as TrafficSource;
      }

      if (isValidFilter(campaignTypeFilter)) {
        params.company_type = campaignTypeFilter as 'free' | 'free trial';
      }

      if (sortBy) {
        params.sort_by = sortBy;
      }

      const response = await refShareApi.getOrders(params);
      setOrders(response.objects || []);
      setTotalCount(response.count || 0);
      setTotalPlatformIncome(Number(response.transactions_total_5_percent || 0));
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      showError(parseApiError(error, 'Не удалось загрузить кампании'));
      setOrders([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, search, statusFilter, ownerTypeFilter, modelFilter, sourceFilter, campaignTypeFilter, sortBy]);

  // Fetch OnlyFans accounts for model filter
  const fetchOnlyFansAccounts = useCallback(async (searchTerm?: string) => {
    setIsLoadingModels(true);
    try {
      const params: Record<string, unknown> = {
        page: 1,
        page_size: 20,
      };
      if (searchTerm?.trim()) {
        params.search = searchTerm.trim();
      }
      const response = await staffApi.getOnlyFansAccounts(params);
      setOnlyFansAccounts(response.objects || []);
    } catch (error) {
      console.error('Failed to fetch OnlyFans accounts:', error);
      setOnlyFansAccounts([]);
    } finally {
      setIsLoadingModels(false);
    }
  }, []);

  // Fetch orders on filter/pagination change
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Fetch OnlyFans accounts on mount and when search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOnlyFansAccounts(modelSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchOnlyFansAccounts, modelSearchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, campaignTypeFilter, ownerTypeFilter, modelFilter, sourceFilter]);

  // Status options (matching backend OrderStatus values)
  const statusOptions = useMemo(() => [
    { key: 'all', label: t('admin.revshareCampaigns.filters.status.all') },
    { key: 'in_progress', label: t('admin.revshareCampaigns.filters.status.active') },
    { key: 'in_moderation', label: t('admin.revshareCampaigns.filters.status.moderation') },
    { key: 'completed', label: "Выполнена" },
    { key: 'canceled', label: t('admin.revshareCampaigns.filters.status.cancelled') },
  ], [t]);

  // Campaign type options (free, free trial, link)
  const campaignTypeOptions = useMemo(() => [
    { key: 'all', label: t('admin.revshareCampaigns.filters.campaignType.all') },
    { key: 'free', label: 'FREE' },
    { key: 'free trial', label: 'Free-trial' },
  ], [t]);

  // Owner type options
  const ownerTypeOptions = useMemo(() => [
    { key: 'all', label: 'Все владельцы' },
    { key: 'creator', label: t('admin.revshareCampaigns.filters.owner.creator') },
    { key: 'agency', label: t('admin.revshareCampaigns.filters.owner.agency') },
  ], [t]);

  // Source options
  const sourceOptions = useMemo(() => [
    { key: 'all', label: t('admin.revshareCampaigns.filters.source.all') },
    { key: 'instagram', label: 'Instagram' },
    { key: 'tiktok', label: 'TikTok' },
    { key: 'twitter', label: 'Twitter' },
    { key: 'reddit', label: 'Reddit' },
    { key: 'telegram', label: 'Telegram' },
    { key: 'youtube', label: 'YouTube' },
    { key: 'google', label: 'Google' },
    { key: 'other', label: t('admin.revshareCampaigns.filters.source.other') },
  ], [t]);

  // Model options from OnlyFans accounts API
  const modelOptions = useMemo(() => {
    return [
      { key: 'all', label: t('admin.revshareCampaigns.filters.model.all') },
      ...onlyFansAccounts.map((account) => ({
        key: String(account.of_user_id),
        label: account.name || account.of_user_name,
        avatar: account.avatar,
        name: account.name,
        username: account.of_user_name,
      })),
    ];
  }, [onlyFansAccounts, t]);

  const handleReset = useCallback(() => {
    setSearch('');
    setStatusFilter('all');
    setCampaignTypeFilter('all');
    setOwnerTypeFilter('all');
    setModelFilter('all');
    setSourceFilter('all');
  }, []);

  const hasActiveFilters = search !== '' ||
    statusFilter !== 'all' ||
    campaignTypeFilter !== 'all' ||
    ownerTypeFilter !== 'all' ||
    modelFilter !== 'all' ||
    sourceFilter !== 'all';

  // Handle status change from table actions
  const handleStatusChange = useCallback(async (orderId: string, newStatus: OrderStatus) => {
    try {
      await refShareApi.changeOrderStatus(orderId, newStatus);
      // Refetch orders to update the list
      fetchOrders();
    } catch (error) {
      showError(parseApiError(error, 'Не удалось изменить статус кампании'));
    }
  }, [fetchOrders]);

  // Handle sort change
  const handleSort = useCallback((newSortBy: string) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
  }, []);

  return (
    <div className="h-screen flex flex-col">
      <div className="flex-1">
        <div className="px-[24px] py-[24px]">
          <div className="mb-[16px]">
            <Typography variant="h5" className="text-[#11181C] font-semibold mb-[16px]">
              {t('admin.revshareCampaigns.title')}
            </Typography>

            <div className="flex items-center gap-[16px] flex-wrap">
              <Input
                type="text"
                placeholder={"Поиск по ID компании"}
                size="sm"
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                endContent={<SearchInputIc />}
                classNames={{
                  mainWrapper: "w-[273px]",
                  base: "w-[273px]",
                  input: "text-sm",
                  inputWrapper: "rounded-lg h-[36px] w-[273px]",
                }}
              />

              <FilterSelect
                selectedKey={statusFilter}
                onSelectionChange={(val) => setStatusFilter(val || 'all')}
                options={statusOptions}
                placeholder={t('admin.revshareCampaigns.filters.status.placeholder')}
                variant="campaign"
                autoWidth={true}
                startContent={<DangerIc size={20} />}
              />

              <FilterSelect
                selectedKey={ownerTypeFilter}
                onSelectionChange={(val) => setOwnerTypeFilter(val || 'all')}
                options={ownerTypeOptions}
                placeholder={t('admin.revshareCampaigns.filters.owner.placeholder')}
                variant="campaign"
                autoWidth={true}
              />

              <FilterSelect
                selectedKey={sourceFilter}
                onSelectionChange={(val) => setSourceFilter(val || 'all')}
                options={sourceOptions}
                placeholder={t('admin.revshareCampaigns.filters.source.placeholder')}
                variant="campaign"
                autoWidth={true}
              />

              <FilterSelect
                selectedKey={campaignTypeFilter}
                onSelectionChange={(val) => setCampaignTypeFilter(val || 'all')}
                options={campaignTypeOptions}
                placeholder={t('admin.revshareCampaigns.filters.campaignType.placeholder')}
                variant="campaign"
                autoWidth={true}
              />

              <ModelSearchSelect
                selectedKey={modelFilter}
                onSelectionChange={(val) => setModelFilter(val || 'all')}
                onSearchChange={setModelSearchTerm}
                options={modelOptions}
                placeholder={t('admin.revshareCampaigns.filters.model.placeholder')}
                variant="campaign"
                autoWidth={true}
                isLoading={isLoadingModels}
              />

              {hasActiveFilters && (
                <Tooltip content={t('admin.revshareCampaigns.filters.resetTooltip')} placement="top">
                  <Button
                    size="sm"
                    className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
                    onPress={handleReset}
                  >
                    <ResetFiltersIc />
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          <RevshareCampaignsTable
            orders={orders}
            pagination={{
              count: totalCount,
              page_size: pageSize,
              page: currentPage,
            }}
            isLoading={isLoading}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            onStatusChange={handleStatusChange}
            totalPlatformIncome={totalPlatformIncome}
            sortBy={sortBy}
            onSort={handleSort}
          />
        </div>
      </div>
    </div>
  );
};

export default RevshareCampaignsPage;
